import { FileText, Building2, RefreshCw } from "lucide-react";

const DigitalInfrastructure = () => {
  return (
    <>
      {/* SECTION 1: DHRUVA - White Background */}
      <section className="py-16 md:py-24 bg-white" data-testid="digital-infrastructure-section">
        <div className="container mx-auto px-4 lg:px-8 max-w-[1150px] relative">
          
          {/* Micro Badge - Top Right */}
          <div className="absolute top-0 right-4 lg:right-8">
            <span className="text-[11px] font-medium text-[#1F7A5A] bg-[#E8F2ED] px-3 py-1.5 rounded">
              Government-Recognised Platform
            </span>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-5 gap-10 lg:gap-16 pt-8">
            
            {/* LEFT COLUMN - Text (60%) */}
            <div className="lg:col-span-3" data-testid="dhruva-text-column">
              {/* Title */}
              <h2 className="font-display text-3xl md:text-4xl font-bold text-[#1F7A5A] mb-2">
                DHRUVA
              </h2>
              
              {/* Subtitle */}
              <p className="text-[#6E6E6E] text-base mb-8">
                Institutional Digital Infrastructure for Aftercare
              </p>

              {/* Core Capabilities */}
              <div className="space-y-6 mb-8">
                <div>
                  <h4 className="font-semibold text-[#1E1E1E] text-sm mb-1">
                    Verified Beneficiary Registry
                  </h4>
                  <p className="text-[#6E6E6E] text-sm">
                    A structured digital registry of care-leavers with authenticated identity records.
                  </p>
                </div>

                <div>
                  <h4 className="font-semibold text-[#1E1E1E] text-sm mb-1">
                    Unique ID–Based Tracking
                  </h4>
                  <p className="text-[#6E6E6E] text-sm">
                    Each registered care-leaver is issued a unique alphanumeric ID linked to services and transition pathways.
                  </p>
                </div>

                <div>
                  <h4 className="font-semibold text-[#1E1E1E] text-sm mb-1">
                    Longitudinal Engagement Framework
                  </h4>
                  <p className="text-[#6E6E6E] text-sm">
                    Continuity of support tracked across education, employability, and aftercare stages.
                  </p>
                </div>

                <div>
                  <h4 className="font-semibold text-[#1E1E1E] text-sm mb-1">
                    CSR-Aligned Reporting Infrastructure
                  </h4>
                  <p className="text-[#6E6E6E] text-sm">
                    Designed to support institutional accountability, measurable engagement, and scalable partnerships.
                  </p>
                </div>
              </div>

              {/* Institutional Recognition */}
              <p className="text-[#6E6E6E] text-sm mb-6 border-t border-[#E5E7EB] pt-6">
                Recognised through official communication from the Central Government and written endorsement letters from 13 State Governments.
              </p>

              {/* Closing Confidence Line */}
              <p className="text-[#1F7A5A] font-medium text-sm">
                DHRUVA enables traceability, scale, and responsible long-term institutional engagement.
              </p>
            </div>

            {/* RIGHT COLUMN - Clean Process Strip (40%) */}
            <div className="lg:col-span-2 flex items-center justify-center" data-testid="dhruva-visual-column">
              {/* Clean 3-Point Horizontal Process */}
              <div className="w-full max-w-[300px]">
                <div className="flex items-center justify-between">
                  {/* Step 1: Registry */}
                  <div className="flex flex-col items-center">
                    <div className="w-16 h-16 rounded-full bg-[#E8F2ED] border-2 border-[#1F7A5A] flex items-center justify-center mb-2">
                      <span className="text-[#1F7A5A] text-xs font-semibold">01</span>
                    </div>
                    <p className="text-xs font-semibold text-[#1F7A5A]">Registry</p>
                    <p className="text-[10px] text-[#6E6E6E] text-center mt-0.5">Verified ID</p>
                  </div>
                  
                  {/* Arrow 1 */}
                  <div className="flex-1 flex items-center justify-center">
                    <div className="w-8 h-0.5 bg-[#1F7A5A]"></div>
                    <div className="w-0 h-0 border-t-4 border-b-4 border-l-6 border-transparent border-l-[#1F7A5A]"></div>
                  </div>
                  
                  {/* Step 2: Tracking */}
                  <div className="flex flex-col items-center">
                    <div className="w-16 h-16 rounded-full bg-[#E8F2ED] border-2 border-[#1F7A5A] flex items-center justify-center mb-2">
                      <span className="text-[#1F7A5A] text-xs font-semibold">02</span>
                    </div>
                    <p className="text-xs font-semibold text-[#1F7A5A]">Tracking</p>
                    <p className="text-[10px] text-[#6E6E6E] text-center mt-0.5">Longitudinal</p>
                  </div>
                  
                  {/* Arrow 2 */}
                  <div className="flex-1 flex items-center justify-center">
                    <div className="w-8 h-0.5 bg-[#1F7A5A]"></div>
                    <div className="w-0 h-0 border-t-4 border-b-4 border-l-6 border-transparent border-l-[#1F7A5A]"></div>
                  </div>
                  
                  {/* Step 3: Reporting */}
                  <div className="flex flex-col items-center">
                    <div className="w-16 h-16 rounded-full bg-[#1F7A5A] flex items-center justify-center mb-2">
                      <span className="text-white text-xs font-semibold">03</span>
                    </div>
                    <p className="text-xs font-semibold text-[#1F7A5A]">Reporting</p>
                    <p className="text-[10px] text-[#6E6E6E] text-center mt-0.5">CSR-Ready</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* SECTION 2: Government Recognition Band - Muted Green */}
      <section className="py-14 md:py-20 bg-[#F4F8F6]" data-testid="govt-recognition-section">
        <div className="container mx-auto px-4 lg:px-8 max-w-[1150px]">
          
          {/* Title */}
          <h3 className="font-display text-xl md:text-2xl font-bold text-[#1E1E1E] text-center mb-12">
            Government Recognition & Institutional Validation
          </h3>

          {/* Three Validation Pillars */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 md:gap-12 mb-10">
            
            {/* Pillar 1: Central Government */}
            <div className="text-center" data-testid="validation-pillar-1">
              <div className="w-12 h-12 mx-auto mb-4 flex items-center justify-center">
                <Building2 className="w-8 h-8 text-[#1F7A5A]" strokeWidth={1.5} />
              </div>
              <h4 className="font-semibold text-[#1E1E1E] text-sm mb-2">
                Central Government Communication
              </h4>
              <p className="text-[#6E6E6E] text-sm">
                Formal acknowledgement issued through official correspondence.
              </p>
            </div>

            {/* Pillar 2: State Endorsements */}
            <div className="text-center" data-testid="validation-pillar-2">
              <div className="w-12 h-12 mx-auto mb-4 flex items-center justify-center">
                <FileText className="w-8 h-8 text-[#1F7A5A]" strokeWidth={1.5} />
              </div>
              <h4 className="font-semibold text-[#1E1E1E] text-sm mb-2">
                13 State Government Endorsements
              </h4>
              <p className="text-[#6E6E6E] text-sm">
                Written letters supporting structured care-leaver data integration.
              </p>
            </div>

            {/* Pillar 3: State-Level Propagation */}
            <div className="text-center" data-testid="validation-pillar-3">
              <div className="w-12 h-12 mx-auto mb-4 flex items-center justify-center">
                <RefreshCw className="w-8 h-8 text-[#1F7A5A]" strokeWidth={1.5} />
              </div>
              <h4 className="font-semibold text-[#1E1E1E] text-sm mb-2">
                State-Level Propagation
              </h4>
              <p className="text-[#6E6E6E] text-sm">
                Encouraged use of DHRUVA as a credible digital registry framework.
              </p>
            </div>
          </div>

          {/* Footnote */}
          <p className="text-center text-[#6E6E6E] text-xs">
            Official letters available for CSR committee review upon request.
          </p>
        </div>
      </section>
    </>
  );
};

export default DigitalInfrastructure;
